/**
 * The hub's LAN protocol (08-clients.md "Outlet hub"): what tills, captains, KDS screens
 * and peer hubs send and receive. Browser-safe (zod only): the staff app imports this and
 * `client.ts`, never the server.
 *
 * Auth: every call except `GET /hub/info` carries `X-Terminal-Id` and
 * `Authorization: Bearer <token>`, where the token is `HMAC-SHA256(hubSecret, terminalId)`
 * issued to the terminal when it was registered with the outlet (see auth.ts). The hub
 * secret is runtime configuration, never code.
 */
import { kdsTicketSchema, printDocSchema, syncCommandSchema } from '@qrkey/contracts';
import { z } from 'zod';

export const HUB_SERVICE_TYPE = '_qrkey-hub._tcp';
export const HUB_PROTOCOL_VERSION = 1;
/** A hub silent this long is dead and the next `hub_priority` takes over (08-clients.md). */
export const HUB_SILENCE_MS = 30_000;

export const HUB_ROLES = ['leader', 'standby'] as const;
export type HubRole = (typeof HUB_ROLES)[number];

/** `GET /hub/info`: public, carries nothing secret. */
export const hubInfoSchema = z.object({
  v: z.literal(HUB_PROTOCOL_VERSION),
  outletId: z.string(),
  nodeId: z.string(),
  priority: z.int(),
  role: z.enum(HUB_ROLES),
  /** Caught up with every live peer: only a ready node may take the lead. */
  ready: z.boolean(),
  /** The cloud answered the hub's last upload. */
  cloudOnline: z.boolean(),
  pending: z.int(),
});
export type HubInfo = z.output<typeof hubInfoSchema>;

/** A KDS ticket as the terminal built it; the hub numbers it when `kotNo` is absent. */
export const hubTicketInputSchema = kdsTicketSchema.extend({ kotNo: z.int().min(1).optional() });
export type HubTicketInput = z.output<typeof hubTicketInputSchema>;

/**
 * `POST /commands`. The command is exactly what `{o}/sync` takes (and keeps its
 * Idempotency-Key). `order` is the terminal's copy of the order after the command, for
 * the hub's open-orders view and the broadcast to the other devices; `tickets` are the
 * KOT tickets a send produced, for the KDS screens.
 */
export const submitCommandSchema = z.object({
  terminalId: z.string().min(1),
  command: syncCommandSchema,
  order: z.object({ id: z.string(), version: z.int().nullable().optional() }).loose().optional(),
  tickets: z.array(hubTicketInputSchema).max(20).optional(),
});
export type SubmitCommand = z.input<typeof submitCommandSchema>;

export const submitResultSchema = z.object({
  accepted: z.boolean(),
  /** The key was already in the log: nothing changed. */
  duplicate: z.boolean(),
  /** The KOT number the hub assigned from its own range, when it did. */
  kotNo: z.int().nullable(),
  seq: z.int(),
});
export type SubmitResult = z.output<typeof submitResultSchema>;

export const printRequestSchema = z.object({
  /** The caller's job id: asking twice with the same id prints once. */
  jobId: z.string().min(1),
  printerId: z.string().min(1),
  doc: printDocSchema,
});
export type PrintRequest = z.input<typeof printRequestSchema>;

export const PRINT_STATES = ['queued', 'sent', 'printed', 'failed'] as const;
export type PrintState = (typeof PRINT_STATES)[number];

export const printStatusSchema = z.object({
  jobId: z.string(),
  printerId: z.string(),
  state: z.enum(PRINT_STATES),
  attempts: z.int(),
  error: z.string().nullable(),
});
export type PrintStatus = z.output<typeof printStatusSchema>;

export const printResultSchema = z.object({
  ok: z.boolean(),
  error: z.string().max(300).optional(),
});

export const kdsActionSchema = z.object({
  action: z.enum(['start', 'bump', 'recall']),
  /** Bump only these items (a tap on one item); all of the ticket's when absent. */
  lineIds: z.array(z.string()).max(100).optional(),
});

/** What the hub pushes over `GET /events` (server-sent events). */
export const HUB_TOPICS = ['kds', 'orders', 'print', 'sync', 'hub'] as const;
export type HubTopic = (typeof HUB_TOPICS)[number];

export interface HubEvent {
  /** Monotonic per node; `Last-Event-ID` replays what was missed. */
  id: number;
  topic: HubTopic;
  type: string;
  data: unknown;
  /** Set for per-terminal messages (print jobs for a printer a terminal owns). */
  to?: string;
}
